/**
 * UpdateBanner — Phase 7a (14 Jun 2026).
 *
 * Prompts the user to reload when a newer service worker is installed
 * and waiting. Without this banner, every guest could sit on stale
 * code indefinitely: public/sw.js shipped a `SKIP_WAITING` message
 * handler but no UI was sending the message. Premium correctness:
 * a deploy must reach every guest within one visit.
 *
 * Mounted globally from App.tsx so it persists across navigation.
 * Fixed positioning at the top of the viewport — high-affordance
 * but non-modal, so the user can keep doing whatever they were
 * doing until they choose to update.
 *
 * Two user actions:
 *
 *   - "Reload to update" → applyUpdate() → SW receives SKIP_WAITING
 *     → activates → controllerchange fires → page reloads onto the
 *     fresh SW. Single tap, no further confirmation.
 *
 *   - Dismiss (X) → hides for this session only. The banner reappears
 *     on the next mount if the update is still waiting. Persistent
 *     "never show again" is deliberately NOT offered — that would
 *     let a guest permanently block updates, which is the exact
 *     scenario this banner exists to fix.
 *
 * EN strings live in the existing `common` namespace under the
 * `updateBanner.*` sub-key (matching the established `offline.*`
 * and `push.*` organisation). FR/DE/ES translations ship in 7b.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, X } from 'lucide-react';
import { useUpdateAvailable } from '../lib/swUpdate';

export function UpdateBanner() {
  const { t } = useTranslation('common');
  const { updateAvailable, applyUpdate } = useUpdateAvailable();
  const [dismissed, setDismissed] = useState(false);

  if (!updateAvailable) return null;
  if (dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-50 border-b border-moor-800 bg-moor-700 text-cream-50 shadow-md"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="container-narrow flex items-center gap-3 px-4 py-3">
        <RefreshCw className="h-4 w-4 shrink-0" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-sm">
          {t('updateBanner.message')}
        </p>
        <button
          type="button"
          onClick={applyUpdate}
          className="shrink-0 rounded-full bg-cream-50 px-3 py-1.5 text-xs font-medium text-moor-900 transition-colors hover:bg-cream-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-cream-50/40"
        >
          {t('updateBanner.reload')}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label={t('updateBanner.dismissAriaLabel')}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-cream-200 transition-colors hover:bg-moor-800 hover:text-cream-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-cream-50/40"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
